import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { listPosts } from '../api/community';

// Body excerpt — collapses whitespace, takes first N chars.
function excerpt(body, max = 150) {
  if (!body) return '';
  const trimmed = body.replace(/\s+/g, ' ').trim();
  return trimmed.length > max ? trimmed.slice(0, max) + '...' : trimmed;
}

function formatDate(iso) {
  if (!iso) return '';
  return iso.slice(0, 10);
}

const cardStyle = {
  background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius)', padding: 'clamp(16px, 3vw, 32px)',
  minHeight: 160, display: 'flex', flexDirection: 'column',
  justifyContent: 'center',
  animation: 'fadeIn 0.4s ease',
};

const arrowStyle = {
  background: 'var(--surface-elev)', border: '1px solid var(--border)',
  borderRadius: '50%', width: 40, height: 40,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', color: 'var(--text)', fontSize: '1.2rem',
};

export default function ReviewSlider() {
  const [posts, setPosts] = useState([]);
  const [state, setState] = useState('loading'); // loading | ok | empty | error
  const [idx, setIdx] = useState(0);

  // Fetch latest review posts once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listPosts({ category: 'review' });
        if (cancelled) return;
        const top = (res.posts || []).slice(0, 5);
        setPosts(top);
        setState(top.length === 0 ? 'empty' : 'ok');
      } catch {
        if (cancelled) return;
        setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-slide every 5s when 2+ reviews.
  useEffect(() => {
    if (posts.length < 2) return undefined;
    const timer = setInterval(() => setIdx(i => (i + 1) % posts.length), 5000);
    return () => clearInterval(timer);
  }, [posts.length]);

  if (state === 'loading') {
    return (
      <div style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto' }}>
        <div style={cardStyle}>
          <p style={{ color: 'var(--text-muted)' }}>후기를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto' }}>
        <div style={cardStyle}>
          <p style={{ color: 'var(--text-muted)' }}>
            후기를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.
          </p>
        </div>
      </div>
    );
  }

  if (state === 'empty') {
    return (
      <div style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto' }}>
        <div style={cardStyle}>
          <p style={{ color: 'var(--text-muted)' }}>아직 등록된 후기가 없습니다.</p>
          <Link to="/community/review" style={{ color: 'var(--primary)', marginTop: 12 }}>
            후기 게시판 보기 →
          </Link>
        </div>
      </div>
    );
  }

  const post = posts[idx];
  const len = posts.length;
  const prev = () => setIdx((idx - 1 + len) % len);
  const next = () => setIdx((idx + 1) % len);

  return (
    <div style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto' }}>
      <div style={cardStyle} key={post.postId}>
        <p style={{ fontSize: '1.05rem', lineHeight: 1.6, margin: '0 0 12px 0' }}>
          "{excerpt(post.body)}"
        </p>
        {post.body && post.body.length > 150 && (
          <Link
            to={`/community/post/${encodeURIComponent(post.postId)}`}
            style={{ color: 'var(--primary)', fontSize: '0.9rem', marginBottom: 12 }}
          >
            더보기 →
          </Link>
        )}
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: 8 }}>
          — {post.nickname || '익명'} · {formatDate(post.createdAt)}
        </p>
      </div>

      <div style={{
        display: 'flex', justifyContent: 'center',
        gap: 16, marginTop: 20,
      }}>
        {len > 1 && (
          <button onClick={prev} style={arrowStyle} aria-label="이전 후기">‹</button>
        )}
        <span style={{ color: 'var(--text-dim)', alignSelf: 'center', fontSize: '0.9rem' }}>
          {idx + 1} / {len}
        </span>
        {len > 1 && (
          <button onClick={next} style={arrowStyle} aria-label="다음 후기">›</button>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <Link
          to="/community/review"
          style={{ color: 'var(--primary)', fontSize: '0.9rem' }}
        >
          후기 더 보기 →
        </Link>
      </div>
    </div>
  );
}
